import { createFileRoute, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { isQaLane } from '@astro/shared-utilities/qa-lane';
import { useTrackingParams } from '@/hooks/useTrackingParams';
import { useStepViewed } from '@/hooks/useLoginTracking';
import { EventName } from '@/lib/constants';
import { resolveLoginTarget } from '@/lib/previewHost';
import { LoginLayout } from '../login/-components/LoginLayout';
import { LoginCard } from '../login/-components/LoginCard';
import { LoginProgress } from '../login/-components/LoginProgress';
import { InformLoginErrorCard } from './-components/InformLoginErrorCard';
import { useInformTokenLogin } from './-hooks/useInformTokenLogin';
import { sanitizeRedirectPath } from './-utils/sanitizeRedirectPath';

const tokenLoginSearchSchema = z
  .object({
    inform_token: z.string().optional(),
    otto_partner: z.string().optional(),
    otto_source: z.string().optional(),
    otto_version: z.coerce.string().optional(),
    otto_content: z.string().optional(),
    // In-app Flow path to land on after the migrate-then-provision chain, e.g.
    // /pilot/copyinsight/{conversationId} for the insight email. Absent for the
    // daily digest, which forwards to Flow's default page. Validated by
    // sanitizeRedirectPath before use, and again server-side in provisionUser.
    redirect: z.string().optional(),
    // Opt-in override that lands the authenticated user in astro's own shell
    // instead of handing them to Flow. Engineer/QA affordance; an unrecognised
    // value falls back to the Flow default rather than erroring the route.
    shell: z.enum(['flow', 'astro']).optional(),
    // QA-only dev lane for this link, set by the admin auth-link modal. Lets a tester
    // land on their own lane's Flow instead of the one the API has configured. A lane
    // label, not a host, and validated with the same predicate the API applies.
    qa_lane: z.string().refine(isQaLane).optional().catch(undefined),
  })
  .passthrough();

export const Route = createFileRoute('/tokenlogin/')({
  component: TokenLoginPage,
  validateSearch: tokenLoginSearchSchema,
});

function TokenLoginPage() {
  const searchParams = useSearch({ from: '/tokenlogin/' });
  useTrackingParams(searchParams);

  const { migrationState, isMigrating, exchangeError, recoveryEmail } = useInformTokenLogin({
    informToken: searchParams.inform_token,
    redirectPath: sanitizeRedirectPath(searchParams.redirect),
    target: resolveLoginTarget(searchParams.shell),
    qaLane: searchParams.qa_lane,
  });

  // Fire once when the migration loading screen appears. The error screens fire
  // their own viewed event from within InformLoginErrorCard.
  useStepViewed(EventName.INFORM_LOGIN_MIGRATION_LOADING_VIEWED, isMigrating, {
    surface: 'tokenlogin',
  });

  if (exchangeError) {
    return (
      <LoginLayout>
        <InformLoginErrorCard
          status={exchangeError.status}
          recoveryEmail={exchangeError.recoveryEmail}
        />
      </LoginLayout>
    );
  }

  if (migrationState.kind === 'error') {
    return (
      <LoginLayout>
        <InformLoginErrorCard status={migrationState.status} recoveryEmail={recoveryEmail} />
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <LoginCard title="Log In" hideSupportText>
        <LoginProgress label={isMigrating ? 'Setting you up…' : 'Signing you in…'} />
      </LoginCard>
    </LoginLayout>
  );
}
